import styled from '@emotion/styled';
import { TextStyleRules, RichTextStyle } from '../rules';

type ContentfulRichTextWrapperProps = {
  textStyle?: TextStyleRules;
  enableExtraction?: boolean;
};

export const CTA_LINK_CLASS_NAME = 'rich-text-cta-link';

const getTextStyleRules = (rules?: TextStyleRules) => {
  const hasSpacedParagraphs = rules?.hasSpacedParagraphs ?? true;
  const style = rules?.style ?? RichTextStyle.Primary;
  return { hasSpacedParagraphs, style };
};

const setColor = (rules?: TextStyleRules) => {
  const { style } = getTextStyleRules(rules);
  return style === RichTextStyle.Muted ? 'grey' : '';
};

const setTextColor = (rules?: TextStyleRules) => {
  const { style } = getTextStyleRules(rules);
  return style === RichTextStyle.Alternative ? 'white' : '';
};

const setMarginTop = (rules?: TextStyleRules) => {
  const { hasSpacedParagraphs } = getTextStyleRules(rules);
  return hasSpacedParagraphs ? '1.5rem' : '';
};

export const GridContainer = styled.article<ContentfulRichTextWrapperProps>`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 1.5rem;
  font-size: 1.063rem;
  font-weight: 400;
  line-height: 1.588em;
  color: ${p => setColor(p.textStyle)};

  ${({ theme, enableExtraction }) => enableExtraction && theme.mq('lg')} {
    grid-template-columns: 7fr 5fr;
    column-gap: 2rem;
    align-items: start;
  }

  ${({ theme, enableExtraction }) => enableExtraction && theme.mq('xl')} {
    grid-template-columns: 2fr 1fr;
  }

  p {
    margin: 0;
    white-space: pre-wrap;
    color: ${p => setTextColor(p.textStyle)};
  }

  & > .grid-cell-p + .grid-cell-p p {
    margin-top: ${p => setMarginTop(p.textStyle)};
  }

  /* An embedded CTA button has its own text color. This rule has a higher
     specificity than the button class, so it must not match the button.
     The :where() keeps the specificity at that of "a". Parent components,
     for example the InfoBanner, can then set their own link color. */
  a:not(:where(.${CTA_LINK_CLASS_NAME} a)) {
    color: ${({ theme }) => theme.colors.brand.green140};
  }

  ul,
  ol {
    padding-left: 1.25rem;
    margin: 0;
    color: ${p => setTextColor(p.textStyle)};
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  & > .grid-cell-main + h2,
  & > .grid-cell-p + h2 {
    margin-top: 3rem;
  }

  table {
    width: 100%;
    border: none;
    margin: 1rem 0 3rem;
    font-size: 1rem;

    td {
      padding: 0.625rem;
      border: none;
    }

    th {
      padding: 0.375rem;
      text-align: left;
    }

    tbody > tr:first-of-type td,
    tr th {
      background-color: ${({ theme }) => theme.colors.brand.green100};
      color: ${({ theme }) => theme.colors.neutral.white100};

      a {
        color: inherit;
      }
    }

    tr:nth-of-type(odd) td {
      background-color: ${({ theme }) => theme.colors.neutral.grey20};
    }

    tr:nth-of-type(even) td {
      background-color: ${({ theme }) => theme.colors.neutral.white100};
    }
  }
`;

export const GridCell = styled.div<{
  isSidebar?: boolean;
  isFullWidth?: boolean;
  textStyle?: TextStyleRules;
  gridRow?: number;
}>`
  grid-column: 1;

  &.grid-cell-sidebar {
    margin-bottom: 1.5rem;
  }

  ${({ theme }) => theme.mq('lg')} {
    ${({ gridRow }) => (gridRow !== undefined ? `grid-row: ${gridRow};` : '')}
    grid-column: ${({ isSidebar: sidebar, isFullWidth: fullWidth }) => {
      if (fullWidth) return '1 / -1';
      if (sidebar) return '2';
      return '1';
    }};

    &.grid-cell-sidebar {
      margin-bottom: 0;
    }

    &.grid-cell-sidebar + &.grid-cell-sidebar {
      margin-top: ${p => setMarginTop(p.textStyle)};
    }
  }
`;

export const BoldWrapper = styled.strong`
  font-weight: bold;
`;
export const ItalicWrapper = styled.i`
  font-style: italic;
`;
export const UnderlineWrapper = styled.span`
  text-decoration: underline;
`;
export const EmbeddedWrapper = styled.div`
  margin: 1rem 0 3rem;
`;
